import { useEffect, useState, useCallback } from "react";
import { RefreshCw, Loader2, Flame, TrendingUp, AlertTriangle, Info, ArrowRight, Settings, History, X } from "lucide-react";
import { api } from "@/lib/api";

/** Simple SVG sparkline. `points` is an array of numbers; renders a
 *  smoothed polyline scaled to the container.  Colour is the metric's
 *  own accent so the sparkline reads as "this-tile's bar in miniature". */
function Sparkline({ points = [], stroke = "#f97316", height = 34 }) {
  if (!points.length) return null;
  const max = Math.max(1, ...points);
  const w = 180;
  const h = height;
  const step = w / Math.max(1, points.length - 1);
  const path = points.map((v, i) => {
    const x = (i * step).toFixed(1);
    const y = (h - (v / max) * (h - 4) - 2).toFixed(1);
    return `${i === 0 ? "M" : "L"}${x},${y}`;
  }).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible">
      {/* baseline */}
      <line x1="0" y1={h - 1} x2={w} y2={h - 1} stroke="currentColor" strokeOpacity="0.08" strokeWidth="1" />
      <path d={path} fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* last-point dot */}
      {points.length > 0 && (
        <circle
          cx={((points.length - 1) * step).toFixed(1)}
          cy={(h - (points[points.length - 1] / max) * (h - 4) - 2).toFixed(1)}
          r="2.5" fill={stroke}
        />
      )}
    </svg>
  );
}

const METRIC_META = [
  { key: "narrations",     label: "Narrations",       stroke: "#8b5cf6" }, // violet
  { key: "depth_us",       label: "US depth (SAT/ACT)", stroke: "#0ea5e9" }, // sky
  { key: "depth_nl",       label: "NL depth (VWO/IB)",  stroke: "#f59e0b" }, // amber
  { key: "tariff_applied", label: "Tariff auto-apply",  stroke: "#10b981" }, // emerald
  { key: "course_tips",    label: "Course tips",        stroke: "#f97316" }, // orange
  { key: "cluster5_essay", label: "Cluster5 essays",    stroke: "#a855f7" }, // fuchsia-purple
];

const SEVERITY_STYLES = {
  action:  { icon: AlertTriangle, cls: "bg-rose-50 border-rose-200 text-rose-900" },
  warning: { icon: AlertTriangle, cls: "bg-amber-50 border-amber-200 text-amber-900" },
  info:    { icon: Info,          cls: "bg-sky-50 border-sky-200 text-sky-900" },
};

export default function BurnRateTile() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState(null);
  const [showConfig, setShowConfig] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [r, h] = await Promise.all([
        api.get("/admin/progress/burn-rate"),
        api.get("/admin/progress/bump-limit-history").catch(() => ({ data: null })),
      ]);
      setData(r.data);
      setHistory(h.data || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const featured = data?.featured_recommendation;
  const FeatIcon = featured ? (SEVERITY_STYLES[featured.severity]?.icon || Info) : null;
  const featCls  = featured ? (SEVERITY_STYLES[featured.severity]?.cls || SEVERITY_STYLES.info.cls) : "";

  return (
    <div
      data-testid="tile-burn-rate"
      className="border border-stone-200 rounded-xl bg-white p-5 shadow-[0_1px_0_rgba(0,0,0,0.03)]"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-orange-500" />
          <div>
            <div className="text-sm font-semibold text-stone-900">
              AI content burn-rate
              <span className="ml-2 text-[11px] font-normal text-stone-500">
                trailing 14 days
              </span>
            </div>
            <div className="text-[11px] text-stone-500 mt-0.5">
              Daily field-fills for each headline metric, plus a data-driven recommendation.
            </div>
          </div>
        </div>
        <button
          data-testid="burn-rate-refresh-btn"
          type="button"
          onClick={refresh}
          disabled={loading}
          className="text-stone-500 hover:text-stone-800 disabled:opacity-50"
          title="Refresh"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </button>
      </div>

      {/* Bump-limit history caption — shows last bump + what it delivered */}
      {history?.history?.length > 0 && (
        <div data-testid="bump-history-caption" className="mb-3 flex items-start gap-2 text-[11px] text-stone-500 border-l-2 border-stone-200 pl-2.5">
          <History className="w-3 h-3 mt-0.5 flex-none" />
          <div className="flex-1">
            {(() => {
              const last = history.history[0];
              const t = new Date(last.at).getTime();
              const secs = Math.max(0, Math.round((Date.now() - t) / 1000));
              const ago = secs < 3600 ? `${Math.round(secs/60)}m ago`
                        : secs < 86400 ? `${Math.round(secs/3600)}h ago`
                        : `${Math.round(secs/86400)}d ago`;
              const durTxt = last.duration_nights === 0 ? "persistent"
                           : last.duration_nights > 1 ? `${last.duration_nights} nights`
                           : "1 night";
              return (
                <>
                  Last bumped <span className="font-medium text-stone-700">{ago}</span>
                  {" "}by{" "}
                  <span className="font-mono text-stone-700">{last.by}</span>
                  {" → --limit "}<span className="font-mono text-stone-700">{last.limit}</span>
                  <span className="text-stone-400"> · {durTxt}</span>
                  {typeof last.delivered === "number" && (
                    <>, ran <span className="font-semibold text-stone-800">{last.delivered.toLocaleString()}</span> extracts</>
                  )}
                  {history.active && (
                    <span className="text-emerald-700 font-medium ml-1">
                      · active
                      {history.active.expires_at
                        ? ` until ${new Date(history.active.expires_at).toLocaleDateString(undefined, {month:'short', day:'numeric'})}`
                        : " (persistent)"}
                    </span>
                  )}
                </>
              );
            })()}
          </div>
          {history?.active && (
            <button
              type="button"
              data-testid="clear-active-bump-btn"
              onClick={async () => {
                if (!window.confirm("Clear the active US-depth-wave bump now?")) return;
                try {
                  await api.post("/admin/progress/clear-us-depth-limit", {});
                  refresh();
                } catch (e) { /* ignore */ }
              }}
              title="Clear the active bump immediately"
              className="text-stone-400 hover:text-rose-700 flex-none"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      )}

      {err && (
        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 mb-3">
          {err}
        </div>
      )}

      {/* Featured recommendation banner */}
      {featured && (
        <div
          data-testid="burn-rate-featured-rec"
          className={`border rounded-lg px-3 py-2 text-[13px] leading-snug mb-4 ${featCls}`}
        >
          <div className="flex items-start gap-2">
            {FeatIcon && <FeatIcon className="w-4 h-4 mt-0.5 flex-none" />}
            <div className="flex-1">
              <span className="font-semibold uppercase text-[10px] tracking-wider opacity-75 mr-2">
                {featured.severity === "action" ? "Action" : featured.severity === "warning" ? "Watch" : "Today"}
              </span>
              {featured.text}
            </div>
          </div>
          {featured.actions?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2 pl-6">
              {featured.actions.map((a, i) => (
                <ActionButton key={i} action={a} onDone={refresh} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sparkline grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {METRIC_META.map(m => {
          const series = data?.series?.[m.key] || [];
          const total14 = series.reduce((a, b) => a + b, 0);
          const last3 = series.slice(-3).reduce((a, b) => a + b, 0);
          return (
            <div
              key={m.key}
              data-testid={`burn-rate-metric-${m.key}`}
              className="border border-stone-100 rounded-lg p-3 bg-stone-50/40"
            >
              <div className="flex items-baseline justify-between mb-1">
                <div className="text-[11px] font-medium text-stone-700">{m.label}</div>
                <div className="text-[10px] text-stone-500">
                  {total14.toLocaleString()} in 14d
                </div>
              </div>
              <Sparkline points={series} stroke={m.stroke} />
              <div className="mt-1 text-[10px] text-stone-500 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" style={{ color: m.stroke }} />
                <span>Last 3 days: <span className="font-medium text-stone-700">{last3}</span></span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Backlog summary + full recs list */}
      {data && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[11px]">
          <BacklogBadge label="US depth backlog"  n={data.backlogs?.us_depth} />
          <BacklogBadge label="NL depth backlog"  n={data.backlogs?.nl_depth} />
          <BacklogBadge label="Narration backlog" n={data.backlogs?.narration} />
        </div>
      )}

      {data?.recommendations?.length > 1 && (
        <details className="mt-3 group">
          <summary className="text-[11px] text-stone-500 hover:text-stone-800 cursor-pointer select-none">
            + {data.recommendations.length - 1} more recommendations
          </summary>
          <ul className="mt-2 space-y-2">
            {data.recommendations
              .filter(r => r !== data.featured_recommendation && r?.metric !== data.featured_recommendation?.metric)
              .map((r, i) => (
                <li key={i} className="text-[12px] text-stone-700 flex flex-col gap-1">
                  <div className="flex gap-2">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 flex-none ${
                      r.severity === "action" ? "bg-rose-500" : r.severity === "warning" ? "bg-amber-500" : "bg-sky-500"
                    }`} />
                    <span>
                      <span className="font-mono text-[10px] text-stone-500 mr-1">{r.metric}</span>
                      {r.text}
                    </span>
                  </div>
                  {r.actions?.length > 0 && (
                    <div className="ml-4 flex flex-wrap gap-1.5">
                      {r.actions.map((a, j) => (
                        <ActionButton key={j} action={a} onDone={refresh} small />
                      ))}
                    </div>
                  )}
                </li>
              ))}
          </ul>
        </details>
      )}

      <div className="mt-3 text-[10px] text-stone-400 flex items-center justify-between">
        <span>Recommendations regenerate on every refresh — rule-based (no LLM cost).</span>
        <button
          data-testid="burn-rate-config-toggle"
          type="button"
          onClick={() => setShowConfig(v => !v)}
          className="inline-flex items-center gap-1 text-stone-500 hover:text-stone-800"
        >
          <Settings className="w-3 h-3" />
          {showConfig ? "Hide thresholds" : "Tune thresholds"}
        </button>
      </div>
      {showConfig && <ConfigPanel onSaved={refresh} />}
    </div>
  );
}

function ConfigPanel({ onSaved }) {
  const [cfg, setCfg]         = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [flash, setFlash]       = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/admin/progress/recommendation-config").then(r => {
      if (!alive) return;
      setCfg(r.data.config || {});
      setDefaults(r.data.defaults || {});
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!defaults) return null;

  const fields = [
    { key: "narrations_min_per_day",   label: "Narrations floor (per day)", hint: "Warn if 3-day median drops below this" },
    { key: "depth_us_min_per_day",     label: "US depth floor",             hint: "Warn if daily fills drop below this" },
    { key: "depth_nl_min_per_day",     label: "NL depth floor",             hint: "Same, for NL wave" },
    { key: "depth_fr_min_per_day",     label: "FR depth floor",             hint: "Same, for FR wave" },
    { key: "us_depth_bump_eta_days",   label: "Bump-limit ETA threshold (days)", hint: "Suggest a bump if US completion ETA exceeds this" },
  ];

  const save = async () => {
    setSaving(true); setFlash(null);
    try {
      await api.post("/admin/progress/recommendation-config", cfg);
      setFlash({ ok: true, msg: "Saved" });
      onSaved?.();
    } catch (e) {
      setFlash({ ok: false, msg: e?.response?.data?.detail || e.message || "Failed" });
    } finally {
      setSaving(false);
      setTimeout(() => setFlash(null), 4000);
    }
  };

  return (
    <div data-testid="rec-config-panel" className="mt-3 border border-stone-200 rounded-lg p-3 bg-stone-50/50">
      <div className="text-[11px] font-medium text-stone-700 mb-2">Recommendation thresholds</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {fields.map(f => (
          <label key={f.key} className="text-[11px] text-stone-600">
            <div className="mb-0.5 text-stone-500">{f.label}
              <span className="text-stone-400 ml-1">· default {defaults[f.key]}</span>
            </div>
            <input
              data-testid={`cfg-${f.key}`}
              type="number"
              min="0"
              max="10000"
              value={cfg?.[f.key] ?? ""}
              placeholder={String(defaults[f.key])}
              onChange={e => setCfg({ ...cfg, [f.key]: e.target.value ? parseInt(e.target.value, 10) : "" })}
              className="w-full border border-stone-200 rounded px-2 py-1 text-[12px] font-mono bg-white focus:border-stone-500 outline-none"
            />
            <div className="text-[10px] text-stone-400 mt-0.5">{f.hint}</div>
          </label>
        ))}
      </div>
      <div className="mt-2 flex items-center justify-end gap-2">
        {flash && (
          <span className={`text-[11px] ${flash.ok ? "text-emerald-700" : "text-rose-700"}`}>{flash.msg}</span>
        )}
        <button
          data-testid="rec-config-save"
          type="button"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1 border border-stone-800 bg-stone-800 hover:bg-stone-900 text-white text-[11px] font-medium rounded-md px-3 py-1 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Settings className="w-3 h-3" />}
          Save thresholds
        </button>
      </div>
    </div>
  );
}

function BacklogBadge({ label, n }) {
  return (
    <div className="border border-stone-200 rounded px-2 py-1.5 bg-white flex items-center justify-between">
      <span className="text-stone-500">{label}</span>
      <span className="font-mono font-semibold text-stone-800">
        {typeof n === "number" ? n.toLocaleString() : "—"}
      </span>
    </div>
  );
}

/** One tap-to-action button. `action` shape:
 *   { label, kind: 'post'|'link', endpoint?, body?, confirm?, href? }
 *  Bump-limit actions get a duration picker so operators can commit
 *  to a multi-night burn without re-pressing daily. */
function ActionButton({ action, onDone, small = false }) {
  const [busy, setBusy]       = useState(false);
  const [flash, setFlash]     = useState(null);
  const [showDur, setShowDur] = useState(false);
  const isBumpAction = action.endpoint === "/admin/progress/bump-us-depth-limit";

  const runPost = async (bodyOverride) => {
    if (action.confirm && !window.confirm(action.confirm)) return;
    setBusy(true); setFlash(null); setShowDur(false);
    try {
      const r = await api.post(action.endpoint, bodyOverride || action.body || {});
      setFlash({ ok: true, msg: r.data?.note || "Done" });
      onDone?.();
    } catch (e) {
      setFlash({ ok: false, msg: e?.response?.data?.detail || e.message || "Failed" });
    } finally {
      setBusy(false);
      setTimeout(() => setFlash(null), 6000);
    }
  };

  const handle = async () => {
    if (action.kind === "link" && action.href) {
      window.location.href = action.href;
      return;
    }
    if (action.kind !== "post") return;
    if (isBumpAction) { setShowDur(true); return; }
    runPost();
  };

  const testid = `burn-action-${(action.label || "").toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <>
      <button
        data-testid={testid}
        type="button"
        onClick={handle}
        disabled={busy}
        className={`inline-flex items-center gap-1 rounded-md border border-current bg-white/60 hover:bg-white ${
          small ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]"
        } font-medium transition disabled:opacity-50`}
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
        {action.label}
      </button>
      {showDur && isBumpAction && (
        <div data-testid="bump-duration-picker" className="inline-flex items-center gap-1 text-[10px]">
          {[
            { d: 1, label: "1 night" },
            { d: 7, label: "7 nights" },
            { d: 30, label: "30 nights" },
            { d: 0, label: "Until I stop it" },
          ].map(opt => (
            <button
              key={opt.d}
              type="button"
              data-testid={`bump-dur-${opt.d}`}
              onClick={() => runPost({ ...(action.body || {}), duration_nights: opt.d })}
              className="border border-stone-300 hover:border-stone-800 hover:bg-stone-800 hover:text-white rounded px-1.5 py-0.5 bg-white transition"
            >
              {opt.label}
            </button>
          ))}
          <button type="button" onClick={() => setShowDur(false)} className="text-stone-400 hover:text-stone-800">✕</button>
        </div>
      )}
      {flash && (
        <span className={`text-[10px] ${flash.ok ? "text-emerald-700" : "text-rose-700"}`}>
          {flash.msg}
        </span>
      )}
    </>
  );
}
